import type { CSSProperties, ReactNode } from "react";
import { BRAND } from "@/lib/tokens";
import { Kicker } from "@/components/memo/ui/Kicker";

// Canonical ghost/primary button styles, shared by every Protokolle screen
// (MemoHeader imports ghostButtonStyle from here). ghostButtonStyle keeps the
// exact look originally introduced in MemoHeader; primaryButtonStyle mirrors
// the existing BRAND.blue/#fff submit buttons already used in
// NewMemoDialog/EditSeriesDialog (fixed ground — a brand-blue background
// with white text does not need to flip per theme).
export const ghostButtonStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: "7px 12px",
  fontSize: 12.5,
  color: "var(--ink-1)",
  background: "transparent",
  cursor: "pointer",
};

export const primaryButtonStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  border: "none",
  borderRadius: 8,
  padding: "7px 14px",
  fontSize: 12.5,
  fontWeight: 600,
  color: "#fff",
  background: BRAND.blue,
  cursor: "pointer",
};

interface ProtokolleHeaderProps {
  kicker: { color: string; label: string };
  /** The display title. Usually a plain string; MemoHeader passes a click-to-edit
   *  date control (MemoDateEditor) so the meeting date can be changed in place. */
  title: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  chips?: ReactNode;
}

/** The shared "1A header" skeleton for Übersicht / SeriesPage / MemoPage:
 * Kicker, big display title + optional StatusBadge, right-aligned actions,
 * and an optional row of MetaChips. Pages supply their own Breadcrumb above
 * this — it is intentionally not included here. */
export function ProtokolleHeader({ kicker, title, badge, actions, chips }: ProtokolleHeaderProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <Kicker color={kicker.color} label={kicker.label} />
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 28,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: "var(--ink-1)",
              margin: 0,
            }}
          >
            {title}
          </h1>
          {badge}
        </div>
        {actions ? (
          <div
            style={{
              display: "flex",
              gap: 8,
              flexShrink: 0,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {actions}
          </div>
        ) : null}
      </div>
      {chips ? <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{chips}</div> : null}
    </div>
  );
}
